// Utility functions for determining colors based on values

/**
 * Determines background color class based on evaluation type
 * @param {string} type - The evaluation type (VSJob, VSResume, VSSkillset)
 * @returns {string} - Tailwind CSS class for background color
 */
export const getEvalTypeBgColorClass = (type) => {
  // All boxes have white background now
  return 'bg-white';
};

/**
 * Determines pill styling and text based on evaluation type using brand colors from index.css
 * @param {string} type - The evaluation type (VSJob, VSResume, VSSkillset)
 * @returns {object} - Object containing display text and color classes
 */
export const getEvalTypePillColorClass = (type) => {
  switch (type) {
    case 'VSResume':
      return {
        text: 'Resume',
        colorClasses: 'bg-transparent [color:var(--color-brand-blue)] border border-transparent'
      };
    case 'VSJob':
      return {
        text: 'Job',
        colorClasses: 'bg-transparent [color:var(--color-brand-purple)] border border-transparent'
      };
    case 'VSSkillset':
      return {
        text: 'Custom',
        colorClasses: 'bg-transparent [color:var(--color-brand-gray)] border border-transparent'
      };
    default:
      return {
        text: 'Custom',
        colorClasses: 'bg-transparent [color:var(--color-brand-gray)] border border-transparent'
      };
  }
};

/**
 * Determines color based on grade value
 * @param {number} value - The value to determine color for (0-10)
 * @returns {string} - Hex color code
 */
export const getColorForValue = (value) => {
  if (value <= 4) {
    return '#EF4444'; // Brand red for values <= 4
  } else if (value <= 6) {
    return '#F59E0B'; // Brand yellow for values 5-6
  } else if (value <= 8) {
    return '#7FE3D9'; // Light shade of brand green for values 7-8
  } else {
    return '#33C6B6'; // Brand green for values 9-10
  }
};

/**
 * Determines color based on seniority level
 * @param {string} level - The seniority level
 * @returns {string} - Hex color code
 */
export const getColorForSeniority = (level) => {
  const lowerLevel = level?.toLowerCase() || '';
  if (lowerLevel.includes('junior')) {
    return '#93c5fd'; // Light blue for Junior
  } else if (lowerLevel.includes('middle')) {
    return '#22c55e'; // Green for Middle
  } else if (lowerLevel.includes('senior')) {
    return '#3b82f6'; // Strong blue for Senior
  } else {
    return '#6b7280'; // Gray for unknown or other levels
  }
};

/**
 * Determines background color class based on quiz score percentage
 * @param {number} score - The quiz score percentage (0-100)
 * @returns {string} - Tailwind CSS class for background color
 */
export const getQuizScoreBgColorClass = (score) => {
  if (score >= 80) {
    return 'bg-green-100'; // Green for scores >= 80%
  } else if (score >= 50) {
    return 'bg-yellow-100'; // Yellow for scores 50-79%
  } else {
    return 'bg-red-100'; // Red for scores < 50%
  }
};

/**
 * Determines text color class based on quiz score percentage
 * @param {number} score - The quiz score percentage (0-100)
 * @returns {string} - Tailwind CSS class for text color
 */
export const getQuizScoreTextColorClass = (score) => {
  if (score >= 80) {
    return 'text-green-600'; // Green for scores >= 80%
  } else if (score >= 50) {
    return 'text-yellow-600'; // Yellow for scores 50-79%
  } else {
    return 'text-red-600'; // Red for scores < 50%
  }
};

/**
 * Determines background color class based on cheating probability
 * @param {number} probability - The cheating probability percentage (0-100)
 * @returns {string} - Tailwind CSS class for background color using brand colors
 */
export const getCheatingProbabilityBgColorClass = (probability) => {
  if (probability >= 70) {
    return '[background-color:var(--color-brand-red-light)]'; // Brand red light for high probability
  } else if (probability >= 30) {
    return '[background-color:var(--color-brand-yellow-light)]'; // Brand yellow light for medium probability
  } else {
    return '[background-color:var(--color-brand-green-light)]'; // Brand green light for low probability
  }
};

/**
 * Determines text color class based on cheating probability
 * @param {number} probability - The cheating probability percentage (0-100)
 * @returns {string} - Text color class using brand colors
 */
export const getCheatingProbabilityTextColorClass = (probability) => {
  if (probability >= 70) {
    return '[color:var(--color-brand-red)]'; // Brand red for high probability
  } else if (probability >= 40) {
    return '[color:var(--color-brand-yellow)]'; // Brand yellow for medium probability
  } else {
    return '[color:var(--color-brand-green)]'; // Brand green for low probability
  }
};

/**
 * Determines label based on cheating probability
 * @param {number} probability - The cheating probability percentage (0-100)
 * @returns {string} - Label (High, Medium, Low)
 */
export const getCheatingProbabilityLabel = (probability) => {
  if (probability >= 70) {
    return 'High';
  } else if (probability >= 30) {
    return 'Medium';
  } else {
    return 'Low';
  }
};

/**
 * Determines text color class based on points (0-5 scale)
 * @param {number} points - The points value (0-5)
 * @returns {string} - Tailwind CSS class for text color
 */
export const getPointsTextColorClass = (points) => {
  if (points >= 4) {
    return 'text-green-600'; // Green for high points (4-5)
  } else if (points >= 2) {
    return 'text-yellow-600'; // Yellow for medium points (2-3)
  } else {
    return 'text-red-600'; // Red for low points (0-1)
  }
};

/**
 * Determines color class for quiz status
 * @param {string} status - The quiz status
 * @returns {string} - Tailwind CSS class for background and text color
 */
export const getQuizStatusColorClass = (status) => {
  switch (status) {
    case 'Not Started':
      return 'bg-gray-100 text-gray-800';
    case 'Started':
      return 'bg-blue-100 text-blue-800';
    case 'Submitted':
      return 'bg-teal-100 text-teal-800';
    case 'Assessing':
      return 'bg-yellow-100 text-yellow-800';
    case 'Done':
      return 'bg-green-100 text-green-800';
    case 'Abandoned':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Determines color class for evaluation status
 * @param {string} status - The evaluation status (es_not_started, es_started, etc.)
 * @returns {string} - Tailwind CSS class for background and text color
 */
export const getEvalStatusColorClass = (status) => {
  switch (status) {
    case 'es_not_started':
      return 'bg-gray-100 text-gray-800';
    case 'es_started':
      return 'bg-blue-100 text-blue-800';
    case 'es_completed':
      return 'bg-green-100 text-green-800';
    case 'es_abandoned':
      return 'bg-red-100 text-red-800';
    case 'es_challenge_completed':
      return 'bg-indigo-100 text-indigo-800';
    case 'es_questionnaire_completed':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Determines color class for evaluation template status (evs_ statuses)
 * @param {string} status - The evaluation template status (evs_available, evs_generating_criteria, etc.)
 * @returns {string} - Tailwind CSS class for background and text color
 */
export const getEvaluationStatusColorClass = (status) => {
  switch (status) {
    case 'evs_available':
      return 'bg-green-100 text-green-800';
    case 'evs_generating_criteria':
    case 'evs_criteria_review':
    case 'evs_generating_questionnaire':
    case 'evs_generating_challenge':
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};
